//framework
import React, { useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { audio } from "services/audioManager";
import { sleep } from "utils/sleep";

//styles
import styles from "./GameOverView.module.scss";

//components
import Button from "components/Shared/Button/Button";

// constants
import { IMAGES } from "constants/constants";
import { MUSIC, VO } from "constants/audio";
import { GAME_STATES, MESSAGES } from "constants/constants";

const MAX_RAISE_PCT = 100; // highest lift (percent of the column's own height)
const MIN_RAISE_PCT = 20;  // lowest lift

// How much each factor contributes to height (must sum to 1)
const W_CASH = 0.5;
const W_ROUND = 0.5;

export default function GameOverView({ gameState, room, roomState, players, sendIfPrimary, isMirror }) {
    const isGameOver = gameState === GAME_STATES.GAME_OVER;
    const showSection = isGameOver;

    // Sort: cash desc; if both zero, furthestRound desc; then name asc
    const sorted = useMemo(() => {
        const arr = (players ?? [])
            .filter((p) => !p.mdmdData?.waitingToJoin)
            .map((p) => ({
                ...p,
                totalMoney: Number(p?.mdmdData?.totalMoney ?? 0),
                furthestRound: Number(p?.mdmdData?.furthestRound ?? 0),
            }));

        arr.sort((a, b) => {
            if (b.totalMoney !== a.totalMoney) return b.totalMoney - a.totalMoney;
            if (a.totalMoney === 0 && b.totalMoney === 0 && b.furthestRound !== a.furthestRound) {
                return b.furthestRound - a.furthestRound;
            }
            return (a.name ?? "").localeCompare(b.name ?? "");
        });

        return arr.map((p, i) => ({ ...p, rank: i + 1 }));
    }, [players]);

    // Check if everyone tied
    const everyoneTied = useMemo(() => {
        if (!sorted.length) return false;

        const firstPlayer = sorted[0];
        return sorted.every(p =>
            p.totalMoney === firstPlayer.totalMoney &&
            p.furthestRound === firstPlayer.furthestRound
        );
    }, [sorted]);

    // Height raise based on weighted cash + round
    const podiumData = useMemo(() => {
        if (!sorted.length) return [];

        // Normalize cash
        const moneyVals = sorted.map((s) => s.totalMoney);
        const minMoney = Math.min(...moneyVals);
        const maxMoney = Math.max(...moneyVals);
        const moneyRange = Math.max(1, maxMoney - minMoney);

        // Normalize furthest round
        const roundVals = sorted.map((s) => s.furthestRound);
        const minRound = Math.min(...roundVals);
        const maxRound = Math.max(...roundVals);
        const roundRange = Math.max(1, maxRound - minRound);

        const withRaise = sorted.map((s) => {
            // If everyone tied, set all podiums to middle height
            if (everyoneTied) {
                const raisePercent = (MAX_RAISE_PCT + MIN_RAISE_PCT) / 2;
                return { ...s, raisePercent };
            }

            const moneyNorm = (s.totalMoney - minMoney) / moneyRange;   // 0..1
            const roundNorm = (s.furthestRound - minRound) / roundRange; // 0..1

            const composite = W_CASH * moneyNorm + W_ROUND * roundNorm; // 0..1
            const raisePercent = MIN_RAISE_PCT + composite * (MAX_RAISE_PCT - MIN_RAISE_PCT);

            return { ...s, raisePercent };
        });

        // Interleave for nice visual staggering (only if not everyone tied)
        if (everyoneTied) {
            return withRaise;
        }

        return withRaise.reduce((acc, item, idx) => (idx % 2 ? [item, ...acc] : [...acc, item]), []);
    }, [sorted, everyoneTied]);

    const titleText = useMemo(() => {
        if (!sorted.length) return "Game Over";

        const topMoney = sorted[0].totalMoney;
        if (topMoney === 0) return "Game Over";

        const winnersCount = sorted.filter(p => p.totalMoney === topMoney).length;
        return winnersCount > 1 ? "It's a Tie!" : `${sorted[0].name} Wins!`;
    }, [sorted]);

    const handlePlayAgainClicked = useCallback(() => {
        if (!sendIfPrimary) return;
        sendIfPrimary(MESSAGES.TO_SERVER.CALL_END_GAME, {});
    }, [sendIfPrimary]);

    const handleReturnToLobbyClicked = useCallback(() => {
        if (!sendIfPrimary) return;
        sendIfPrimary(MESSAGES.TO_SERVER.CHANGE_GAME, {});
    }, [sendIfPrimary]);

    // Background music and vo
    useEffect(() => {
        if (!showSection) return;
        const ac = new AbortController();
        audio.playMusic(MUSIC.GAME_OVER_LOOP, { loop: true, fadeMs: 500, signal: ac.signal });

        const maxTotalMoney = (players ?? []).reduce((max, p) => Math.max(max, Number(p?.mdmdData?.totalMoney) || 0), 0);
        const vo = maxTotalMoney === 1_000_000 ? VO.GAME_OVER_ONE_MILLION : maxTotalMoney > 0 ? VO.GAME_OVER_WITH_WINNERS : VO.GAME_OVER_ALL_OUT;
        if (vo) audio.playVo(vo, { timeoutMs: 8000, signal: ac.signal, stopOnTimeout: true });

        return () => ac.abort();
    }, [showSection]);

    return (
        <section className={styles.gameOverView} data-show={showSection ? "true" : "false"} data-testid="game-over-view">
            <img src={IMAGES.HOST.gameOverBackground} alt="" aria-hidden className={styles.background} />

            <div className={styles.gameOverTitleWrapper}>
                <span className={styles.title} data-text={titleText}>{titleText}</span>
            </div>

            <div className={styles.podiumsRoot}>
                {podiumData.map((player, index) => (
                    <div key={player.id ?? index} className={styles.podiumCol} style={{ "--raise": `${player.raisePercent}%` }} >
                        <div className={styles.avatarWrap} title={player.name ?? "Player"}>
                            {player.avatar && <img src={player.avatar} alt="" aria-hidden className={styles.avatarImg} />}
                        </div>

                        <div className={styles.playerPlate}>
                            <div className={styles.playerName}>{player.name ?? "Player"}</div>

                            {player.totalMoney > 0 ? (
                                <div className={styles.playerMoney}>${player.totalMoney.toLocaleString()}</div>
                            ) : (
                                <div className={styles.playerOut}>Round {player.furthestRound}</div>
                            )}

                            {player.rank <= 3 && player.totalMoney > 0 && (
                                <div className={styles.badge} data-rank={player.rank}>
                                    {player.rank === 1 ? "🥇" : player.rank === 2 ? "🥈" : "🥉"}
                                </div>
                            )}
                        </div>

                        <img src={IMAGES.HOST.gameOverPodium} alt="" aria-hidden className={styles.podiumImage} />
                    </div>
                ))}
            </div>

            <img src={IMAGES.HOST.gameOverForeground} alt="" aria-hidden className={styles.foreground} />

            <div className={styles.buttons} data-show={true} style={isMirror ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
                <Button onClick={handlePlayAgainClicked} className={styles.playAgainButton}>
                    Play Again
                </Button>
                <Button onClick={handleReturnToLobbyClicked} className={styles.returnToLobbyButton}>
                    Return To Lobby
                </Button>
            </div>
        </section>
    );
}

GameOverView.propTypes = {
    gameState: PropTypes.any,
    room: PropTypes.object,
    roomState: PropTypes.object,
    players: PropTypes.array,
};